import React from "react";

import { useColors } from "../context/ThemeContext";
import { mapWalletTransaction } from "../utils/money";

const sv = (d, size = 18, color = "#2EC4A0", sw = 1.5) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d={d} />
  </svg>
);

const IC = {
  down_left: "M17 7L7 17M7 17H17M7 17V7",
  up_right: "M7 17L17 7M17 7H7M17 7V17",
  lock: "M19 11H5A2 2 0 003 13V20A2 2 0 005 22H19A2 2 0 0021 20V13A2 2 0 0019 11ZM7 11V7A5 5 0 0117 7V11",
  bank: "M3 21H21M3 10H21M5 6L12 3L19 6M4 10V21M20 10V21M8 10V21M12 10V21M16 10V21",
};

function txIcon(row, C) {
  if (row.rawType === "contract_lock" || row.rawType === "contract_release" || row.rawType === "contract_return") {
    return sv(IC.lock, 15, row.dir === "in" ? C.lime : C.muted);
  }
  if (row.rawType === "withdrawal" || row.rawType === "withdrawal_reversal") {
    return sv(IC.bank, 15, row.dir === "in" ? C.lime : C.muted);
  }
  if (row.dir === "in") return sv(IC.down_left, 15, C.lime);
  if (row.dir === "fee") return sv(IC.up_right, 15, C.danger);
  return sv(IC.up_right, 15, C.muted);
}

function amountColor(row, C) {
  if (row.dir === "in") return C.lime;
  if (row.dir === "fee") return C.danger;
  return C.text;
}

function statusStyle(status, C) {
  if (status === "pending") return { bg: C.textMid + "22", color: C.textMid, label: "Pending" };
  if (status === "failed") return { bg: C.danger + "22", color: C.danger, label: "Failed" };
  if (status === "reversed") return { bg: C.danger + "22", color: C.danger, label: "Reversed" };
  return null;
}

export default function WalletTransactionList({
  transactions = [],
  limit,
  variant = "wallet",
  emptyMessage = "No wallet transactions yet.",
}) {
  const C = useColors();
  const slice = limit ? transactions.slice(0, limit) : transactions;
  const rows = slice.map(mapWalletTransaction);

  if (!rows.length) {
    return (
      <p style={{ color: C.muted, fontSize: 12, padding: "8px 0 16px", margin: 0 }}>
        {emptyMessage}
      </p>
    );
  }

  if (variant === "home") {
    return rows.map((d, i) => (
      <div
        key={d.id || d.ref || i}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 0",
          borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <div style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            background: d.dir === "in" ? C.lime + "18" : C.forestMid,
            border: `1px solid ${d.dir === "in" ? C.lime + "40" : C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            {txIcon(d, C)}
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: C.text, fontSize: 13, fontWeight: 600, margin: "0 0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {d.name}
            </p>
            <p style={{ color: C.muted, fontSize: 10, margin: 0 }}>
              {d.date}{d.cat ? ` · ${d.cat}` : ""}
            </p>
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
          <p style={{ color: amountColor(d, C), fontSize: 13, fontWeight: 800, margin: "0 0 3px" }}>
            {d.amt}
          </p>
          <span style={{
            background: (d.dir === "in" ? C.lime : d.dir === "fee" ? C.danger : C.muted) + "20",
            color: d.dir === "in" ? C.lime : d.dir === "fee" ? C.danger : C.muted,
            fontSize: 9,
            fontWeight: 800,
            padding: "2px 7px",
            borderRadius: 10,
          }}>
            {d.dir === "in" ? "In" : d.dir === "fee" ? "Fee" : "Out"}
          </span>
        </div>
      </div>
    ));
  }

  return rows.map((t, i) => {
    const badge = variant === "full" ? statusStyle(t.status, C) : null;
    return (
      <div
        key={t.id || t.ref || i}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "11px 0",
          borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : "none",
        }}
      >
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 11,
          flexShrink: 0,
          background: t.dir === "in" ? C.lime + "18" : C.card,
          border: `1px solid ${t.dir === "in" ? C.lime + "40" : C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {txIcon(t, C)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: C.text, fontSize: 12, fontWeight: 600, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t.name}
          </p>
          <p style={{ color: C.muted, fontSize: 10, margin: "2px 0 0" }}>
            {t.date}{variant === "full" && t.cat ? ` · ${t.cat}` : ""}
          </p>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <p style={{ color: amountColor(t, C), fontSize: 13, fontWeight: 800, margin: badge ? "0 0 4px" : 0 }}>
            {t.amt}
          </p>
          {badge && (
            <span style={{
              background: badge.bg,
              color: badge.color,
              fontSize: 9,
              fontWeight: 800,
              padding: "2px 7px",
              borderRadius: 10,
            }}>
              {badge.label}
            </span>
          )}
        </div>
      </div>
    );
  });
}
